import {
  DashboardContainerHeader,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import {
  IDirectoryNode,
  useLazyGetDirectoryItemsQuery,
  useUpdateNodeMutation,
} from 'app/services/documents';
import { IProject } from 'app/services/responseTypes';
import { ITask } from 'app/services/tasks';
import { IUser } from 'app/services/user';
import { ArrowDownIcon, FileIcon, FolderSolidIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import CheckboxInput from 'Components/Form/Inputs/CheckboxInput';
import { NoResultsFound } from 'Components/NoResultsFound';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import usePagination from 'Components/Pagination/usePagination';
import { useClosePopup, useCurrentPopup } from 'Components/Popups';
import Spinner from 'Components/Spinner';
import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from 'utils/AuthProvider';
import { omit, useDocumentsContext } from '.';
import DocumentsFilters from './DocumentsFilters';
import { firstCommonParent } from './MultiNodeActions';

export interface MoveModalProps {
  toBeMoved: IDirectoryNode[];
  lockClient?: IUser;
  lockTask?: ITask;
  lockProject?: IProject;
  onMove: () => void;
}
const MoveModal = () => {
  const closePopup = useClosePopup();
  const { selected, filters } = useDocumentsContext();
  const currentPopup = useCurrentPopup<IDirectoryNode[]>();

  // can select multiple but selected one item from the action popup
  const toBeMoved =
    Object.values(selected).length > 0
      ? Object.values(selected)
      : currentPopup.contextData || [];

  const { lockTask, lockProject, lockClient, setFilters, unselectAll } =
    useDocumentsContext();
  const { user: loggedInUser, isClient } = useAuth();

  const [updateNode, { isLoading: isMoving }] = useUpdateNodeMutation();

  const [currentFolder, setCurrentFolder] = useState<IDirectoryNode>();

  // const parentFolderId = toBeMoved.parent_id;
  const currentFolderId = currentFolder?.id;

  // const isMoveButtonDisabled = currentFolderId === parentFolderId || isLoading;

  const successAndClose = (message: string) => {
    successToast(message);
    currentPopup.unlock();
    closePopup();
  };

  const errorAndClose = (message: string) => {
    errorToast(message);
    currentPopup.unlock();
    closePopup();
  };

  const Move = () => {
    currentPopup.disableBackdrop();
    currentPopup.lock();
    const promises = toBeMoved
      .filter((d) => (d.parent_id || undefined) !== currentFolderId)
      .map((node) =>
        updateNode({
          id: node.id,
          parent_id: currentFolderId || null,
        }).unwrap(),
      );
    Promise.allSettled(promises)
      .then(() => {
        unselectAll();
        successAndClose(`Operation finished successfuly`);
        setFilters({ ...filters, id: firstCommonParent(toBeMoved) });
      })
      .catch(() => errorAndClose(`Couldn't be Moved`));
  };

  return (
    <OverlayWindow
      size={WindowSize.lg + ' h-[40rem]'}
      paddingSize=""
      children={
        <FolderSelector
          clientId={isClient ? loggedInUser?.id : lockClient?.id}
          taskId={lockTask?.id}
          projectId={lockProject?.id}
          parentId={
            toBeMoved.length === 1
              ? toBeMoved[0].parent_id || undefined
              : firstCommonParent(toBeMoved)
          }
          onChange={setCurrentFolder}
        >
          <div className="text-xs text-gray-lighter">
            {'Moving '}
            <span className="font-semibold">
              {toBeMoved.length === 1
                ? toBeMoved[0].title
                : `${toBeMoved.length} items`}
            </span>
            {!isMoving && (
              <>
                {' to '}
                <span className="font-semibold">
                  {currentFolder?.id ? currentFolder?.title : 'Home'}
                </span>
              </>
            )}
          </div>
        </FolderSelector>
      }
      footer={
        <>
          <div className="flex w-full  gap-4 ">
            <Button
              theme="gray-outline"
              disabled={isMoving}
              onClick={closePopup}
            >
              Cancel
            </Button>

            <Button
              theme="green-fill"
              disabled={isMoving}
              isLoading={isMoving}
              onClick={Move}
            >
              Move Here
            </Button>
          </div>
        </>
      }
    />
  );
};

export function FolderSelector({
  clientId,
  parentId,
  projectId,
  children,
  taskId,
  onChange,
  businessId,
  showSelect = true,
}: {
  clientId?: number;
  taskId?: number;
  businessId?: number;
  projectId?: number;
  parentId?: number;
  children?: ReactNode;
  showSelect?: boolean;
  onChange: (directoryId: IDirectoryNode) => void;
}) {
  const { selected, toggleSelectItem, canSelect } = useDocumentsContext();

  const {
    data: nodes,
    filters,
    Pagination,
    updateQueryParamValue,
    setFilters,
    isFetching,
    response,
  } = usePagination(
    useLazyGetDirectoryItemsQuery,
    {
      filters: {
        // initial value
        client_id: clientId,
        task_id: taskId,
        project_id: projectId,
        business_id: businessId,
        // object_type: 'folder',
        id: parentId,
      },
      per_page: 20,
    },
    {
      skipQueryParamsCache: true,
    },
  );
  const currentFolder = response?.file_object;

  useEffect(() => {
    if (currentFolder) onChange(currentFolder);
  }, [currentFolder]);

  return (
    <>
      <DashboardContainerHeader title={children}>
        <div>
          <DocumentsFilters
            directory={currentFolder}
            value={filters}
            onChange={(newFiltersValues) =>
              setFilters({ ...filters, ...newFiltersValues })
            }
          />
        </div>
      </DashboardContainerHeader>
      <ScrollContainer>
        {isFetching && <Spinner />}

        {!isFetching && currentFolder?.id && (
          <div
            className="flex text-sm cursor-pointer hover:bg-gray-400 w-full self-stretch items-center text-gray-dark gap-3 border-b-2 p-2 font-semibold border-gray-800"
            onClick={() =>
              updateQueryParamValue('id', currentFolder?.parent_id || undefined)
            }
          >
            <ArrowDownIcon className="rotate-90 transform text-gray-dark w-3 h-3" />

            {currentFolder.title}
          </div>
        )}

        {!isFetching &&
          (nodes || []).map((item) => (
            <div
              key={item.id}
              className={`${
                item.object_type === 'folder'
                  ? 'cursor-pointer'
                  : 'cursor-default'
              } grid-row folder  hover:bg-gray-700 hover:bg-opacity-5 flex gap-4 px-4 items-center capitalize text-gray-dark border-b  border-gray-100 `}
            >
              {showSelect && (
                <CheckboxInput
                  checked={!!selected[item.id]}
                  disabled={!canSelect(item)}
                  onChange={() => toggleSelectItem(item)}
                />
              )}
              <div
                className="flex  flex-1 w-full self-stretch items-center"
                onClick={() => {
                  if (item.object_type === 'folder') {
                    updateQueryParamValue('id', item.id);
                    onChange(item);
                  }
                }}
              >
                {item.object_type === 'folder' && (
                  <FolderSolidIcon className="w-5 h-5 mr-3" />
                )}
                {item.object_type === 'document' && (
                  <FileIcon className="w-5 h-5 mr-3" />
                )}
                <div className="font-semibold overflow-ellipsis whitespace-pre-wrap text-sm py-4">
                  {item.title}
                </div>
              </div>
            </div>
          ))}
        {!isFetching && nodes?.length === 0 && (
          <NoResultsFound subTitle="Empty Folder" />
        )}
      </ScrollContainer>
      <Pagination />
    </>
  );
}
export default MoveModal;
